/**
 * DataSource Validation Schemas
 *
 * Zod schemas for validating datasource configurations
 */
import { z } from 'zod';
import { DATASOURCE_OPERATIONS } from '../types/datasource-adapter.js';
// =============================================================================
// AUTH CONFIG
// =============================================================================
export const AuthConfigSchema = z.object({
    type: z.enum(['none', 'apiKey', 'basic', 'bearer']),
    keyName: z.string().min(1).max(100).optional(),
    keyLocation: z.enum(['header', 'query']).optional(),
    credentials: z.string().max(500).optional(), // Supports ${ENV_VAR}
}).refine((data) => {
    // If type is apiKey, keyName and credentials are required
    if (data.type === 'apiKey') {
        return !!data.keyName && !!data.credentials && !!data.keyLocation;
    }
    // If type is basic or bearer, credentials are required
    if (data.type === 'basic' || data.type === 'bearer') {
        return !!data.credentials;
    }
    return true;
}, {
    message: 'Invalid auth configuration for the selected type'
});
// =============================================================================
// CACHE CONFIG
// =============================================================================
export const CacheConfigSchema = z.object({
    enabled: z.boolean().default(true),
    ttl: z.object({
        suggest: z.number().int().min(0).max(86400).optional(), // Max 1 day
        search: z.number().int().min(0).max(86400).optional(),
        detail: z.number().int().min(0).max(86400).optional(),
        list: z.number().int().min(0).max(86400).optional(),
        features: z.number().int().min(0).max(86400).optional(), // GeoJSON features
        related: z.number().int().min(0).max(86400).optional(), // Entità correlate (grafo)
        explain: z.number().int().min(0).max(86400).optional(), // Catene di evidenza (grafo)
    }).optional(),
});
// =============================================================================
// ADAPTER OPTIONS
// =============================================================================
/** Placeholder di variabile d'ambiente: l'intera stringa deve essere `${NOME_VAR}` */
export const ENV_PLACEHOLDER_REGEX = /^\$\{[A-Z][A-Z0-9_]*\}$/;
export const WEBHOOK_SECRET_PLACEHOLDER_MESSAGE = "Il segreto va referenziato come variabile d'ambiente, es. ${ARCHIVES_MEIS_WEBHOOK_SECRET}";
/**
 * Opzioni libere dell'adapter. Unico vincolo: `webhookSecret` (segreto HMAC del
 * webhook di invalidazione ArcHives) non deve mai essere salvato in chiaro nella
 * config del progetto, ma solo come placeholder risolto dal site-backend a runtime.
 */
const AdapterOptionsSchema = z.record(z.unknown()).superRefine((options, ctx) => {
    if (!('webhookSecret' in options))
        return;
    const secret = options.webhookSecret;
    if (typeof secret !== 'string' || !ENV_PLACEHOLDER_REGEX.test(secret)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['webhookSecret'],
            message: WEBHOOK_SECRET_PLACEHOLDER_MESSAGE,
        });
    }
});
// =============================================================================
// ADAPTER CONFIG
// =============================================================================
export const AdapterConfigSchema = z.object({
    baseUrl: z.string().url('Invalid base URL'),
    endpoints: z.record(z.string()).optional(),
    auth: AuthConfigSchema.optional(),
    timeout: z.number().int().min(100).max(60000).default(10000), // 100ms to 60s
    options: AdapterOptionsSchema.optional(),
});
// =============================================================================
// DATASOURCE DEFINITION
// =============================================================================
export const DataSourceDefinitionSchema = z.object({
    id: z.string()
        .min(1, 'ID is required')
        .max(50, 'ID too long')
        .regex(/^[a-z0-9-]+$/, 'ID must contain only lowercase letters, numbers, and hyphens'),
    adapter: z.string()
        .min(1, 'Adapter is required')
        .max(50, 'Adapter name too long'),
    config: AdapterConfigSchema,
    cache: CacheConfigSchema.optional(),
    enabledOperations: z.array(z.enum(DATASOURCE_OPERATIONS)).optional(),
});
// =============================================================================
// VALIDATION HELPERS
// =============================================================================
/**
 * Validate datasource definition
 */
export function validateDataSourceDefinition(data) {
    return DataSourceDefinitionSchema.safeParse(data);
}
/**
 * Validate array of datasource definitions
 */
export function validateDataSourceDefinitions(data) {
    return z.array(DataSourceDefinitionSchema).safeParse(data);
}
//# sourceMappingURL=datasource.schema.js.map